/**
 *
 * ChangeEmail
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Modal,
  Alert,
  Button,
  FormGroup,
  ControlLabel,
  FormControl,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Spinner from 'components/Spinner';
import { blue500 } from 'styles/colors';
import { useChangeEmail } from './useChangeEmail';
import MergeAccountsCheckbox from './MergeAccountsCheckbox';
import messages from './messages';

function ChangeEmail({ user, onRequestSuccess }) {
  const {
    show,
    newEmail,
    setNewEmail,
    requesting,
    error,
    mergeAccounts,
    setMergeAccounts,
    handleShow,
    handleClose,
    handleSubmit,
    isValidEmail,
    isEmailAlreadyInUse,
  } = useChangeEmail(user, onRequestSuccess);

  const isSubmitDisabled =
    !isValidEmail || requesting || (isEmailAlreadyInUse && !mergeAccounts);

  const getSubmitButtonLabel = () => {
    if (requesting) {
      return <Spinner />;
    }
    if (isEmailAlreadyInUse && mergeAccounts) {
      return <FormattedMessage {...messages.mergeAccounts} />;
    }
    return <FormattedMessage {...messages.requestChange} />;
  };

  return (
    <>
      <button
        type="button"
        onClick={handleShow}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          marginLeft: '8px',
          cursor: 'pointer',
        }}
        title="Change email address"
      >
        <FontAwesomeIcon
          icon="pen"
          style={{
            color: blue500,
            opacity: 0.65,
          }}
        />
      </button>

      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage {...messages.changeEmailTitle} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            <Alert bsStyle="info">
              <FormattedMessage {...messages.changeEmailInfo} />
            </Alert>

            {error &&
              !isEmailAlreadyInUse && <Alert bsStyle="danger">{error}</Alert>}

            <FormGroup>
              <ControlLabel>
                <FormattedMessage {...messages.newEmailLabel} />
              </ControlLabel>
              <FormControl
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="new@example.com"
                autoFocus
                disabled={requesting}
                readOnly={isEmailAlreadyInUse}
              />
            </FormGroup>

            {isEmailAlreadyInUse && (
              <MergeAccountsCheckbox
                checked={mergeAccounts}
                onChange={e => setMergeAccounts(e.target.checked)}
                disabled={requesting}
              />
            )}

            <div className="text-right mt-3">
              <Button
                bsStyle="default"
                onClick={handleClose}
                disabled={requesting}
                className="mr-2"
              >
                <FormattedMessage {...messages.cancel} />
              </Button>
              <Button
                bsStyle="primary"
                type="submit"
                disabled={isSubmitDisabled}
              >
                {getSubmitButtonLabel()}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}

ChangeEmail.propTypes = {
  user: PropTypes.object.isRequired,
  onRequestSuccess: PropTypes.func,
};

export default ChangeEmail;
